import { connect } from 'react-redux';
import { go, goBack, push } from 'redux-first-history';
import { parsePhoneNumber } from 'libphonenumber-js';
import currencyFormatter from 'currency-formatter';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import OnboardingConfirmationPage from './component';
import { ordinalFormat } from '@common/utils/stringUtils';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import { logError } from '@zg-rentals/log-error';

import {
  editAccountInfoPage,
  editDueDatePage,
  editInviteTenantPage,
  editMoveInCostsPage,
  editPaymentTermPage,
  editRentAmountPage,
  editTaxVerificationPage,
} from '@rental-manager/actions/OnboardingActions';
import { getFeaturePermissions, requestPhoneVerificationCheck } from '@rental-manager/actions/requests';
import { cleanPhoneNumber } from '@common/utils/numbersUtils';
import { closeNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { getAccountLast4 } from '@common/paymentsUtils/accountInfo';
import HTTP_STATUS from '@common/constants/HttpConstants';
import API_MESSAGES from '@rental-manager/constants/ApiMessageConstants';
import paths from '@rental-manager/pathsManifest';
import { generateMoveInCostPayload, getMoveInCost } from '@common/paymentsUtils/moveInCostsUtil';
import type { IdentityVerificationInfo } from '@zg-rentals/ts-rent-payment-client';

const getAccountInfoDetails = ({ landlordStatus, accountInfo, accountInfoFromBackend }) => {
  const accountLast4 = getAccountLast4({ landlordStatus, accountInfo, accountInfoFromBackend });
  return accountLast4;
};

const getBaseMonthlyAmount = ({ monthlyRent }) => [currencyFormatter.format(monthlyRent / 100, { code: 'USD' })];

const getMonthlyPaymentDate = ({ paymentDayOfTheMonth }) => {
  if (paymentDayOfTheMonth === 31) {
    return ['Last day of the month'];
  }
  return [`${ordinalFormat(paymentDayOfTheMonth)} of the month`];
};

const getPaymentTerm = ({ firstDueDate, monthToMonth, paymentEndDate }) => [
  `First full rent payment: ${formatDate(firstDueDate, 'MMMM d, yyyy')}`,
  monthToMonth ? 'No end date/Month-to-month' : `Payment period ends: ${formatDate(paymentEndDate, 'MMMM d, yyyy')}`,
];

const getTenant = ({ tenantFirstName, tenantLastName, emailAddress }) => [
  `${tenantFirstName} ${tenantLastName}`,
  emailAddress,
];

const mapStateToProps = (state, { propertyId, listing, landlordStatus }) => {
  const {
    acceptTerms,
    paymentSettings,
    accountInfo,
    identityVerificationInfo,
    tenantInvitationInfo,
    moveInCostsSettings,
    identityCheckResponse,
  } = state.payments.landlord.onboarding[propertyId];
  const {
    accountInfo: accountInfoFromBackend,
    currentTerm,
    nextTerm,
    selectedLease,
    listingDetails,
  } = state.payments.landlord.property;
  const { paymentsEncodedLeaseId } = selectedLease || {};
  const rentTermsKey = nextTerm?.rentTermsKey || currentTerm.rentTermsKey;
  const baseMonthlyAmount = getBaseMonthlyAmount(paymentSettings);
  const monthlyPaymentDate = getMonthlyPaymentDate(paymentSettings);
  const paymentTerm = getPaymentTerm(paymentSettings);
  const moveInCosts = getMoveInCost(moveInCostsSettings);
  const depositAccount = [getAccountInfoDetails({ landlordStatus, accountInfo, accountInfoFromBackend })];
  const primaryTenant = getTenant(tenantInvitationInfo);
  const listingName = listing.name;
  const hasLandlordOnboarded =
    landlordStatus !== LANDLORD_STATUS.NOT_STARTED && landlordStatus !== LANDLORD_STATUS.EXTERNAL_ACCOUNT_SET;

  return {
    baseMonthlyAmount,
    monthlyPaymentDate,
    paymentTerm,
    moveInCosts,
    depositAccount,
    primaryTenant,
    listingName,
    hasLandlordOnboarded,
    landlordStatus,
    onboardingInfo: {
      acceptTerms,
      paymentSettings,
      accountInfo,
      identityVerificationInfo,
      tenantInvitationInfo,
      moveInCostsSettings,
      rentTermsKey,
      identityCheckResponse,
      paymentsEncodedLeaseId,
    },
    notification: state.payments.landlord.notification,
    identityVerificationInfo,
    tenantInvitationInfo,
    moveInCostsSettings,
    rentTermsKey,
    identityCheckResponse,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
    listingDetailsContactPhone: listingDetails.contactPhone,
    propertyId,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { baseUrl, propertyId, landlordStatus, location } = ownProps;
  return {
    getVerificationPayload: ({ identityVerificationInfo }) => {
      dispatch(closeNotification());

      try {
        // Check if landlord identity verification is needed
        const needToSubmitId =
          landlordStatus === LANDLORD_STATUS.NOT_STARTED ||
          landlordStatus === LANDLORD_STATUS.ONBOARDING_STARTED ||
          landlordStatus === LANDLORD_STATUS.EXTERNAL_ACCOUNT_SET;

        if (!needToSubmitId) {
          return null;
        }

        // Parse phone number
        let formattedPhoneNumber;
        try {
          formattedPhoneNumber = parsePhoneNumber(identityVerificationInfo.phoneNumber, 'US').number;
        } catch (err) {
          logError({
            error: new Error(
              `Unable to parse phone number: ${identityVerificationInfo.phoneNumber} for property ${propertyId}`,
            ),
          });
        }

        const businessStructure =
          identityVerificationInfo.selectedTaxVerificationType === 'single_member_llc' ||
          identityVerificationInfo.selectedTaxVerificationType === 'multi_member_llc'
            ? identityVerificationInfo.selectedTaxVerificationType
            : '';

        const businessType =
          identityVerificationInfo.selectedTaxVerificationType === 'individual' ? 'individual' : 'company';

        const ssn = String(identityVerificationInfo.ssn ?? '');
        const payload: IdentityVerificationInfo = {
          address: String(identityVerificationInfo.address ?? ''),
          businessType,
          city: String(identityVerificationInfo.city ?? ''),
          companyAddress: String(identityVerificationInfo.companyAddress ?? ''),
          companyCity: String(identityVerificationInfo.companyCity ?? ''),
          companyDbaName: String(identityVerificationInfo.companyDbaName ?? ''),
          companyName: String(identityVerificationInfo.companyName ?? ''),
          companyState: String(identityVerificationInfo.companyState ?? ''),
          companyStructure: businessStructure,
          companyTaxId: String(identityVerificationInfo.companyTaxId ?? ''),
          companyZip: String(identityVerificationInfo.companyZip ?? ''),
          dobDay: parseInt(String(identityVerificationInfo.dobDay), 10) || 0,
          dobMonth: parseInt(String(identityVerificationInfo.dobMonth), 10) || 0,
          dobYear: parseInt(String(identityVerificationInfo.dobYear), 10) || 0,
          firstName: String(identityVerificationInfo.firstName ?? ''),
          lastName: String(identityVerificationInfo.lastName ?? ''),
          phoneNumber: formattedPhoneNumber ?? '',
          ssn,
          ssn4: ssn.slice(-4),
          state: String(identityVerificationInfo.state ?? ''),
          zip: String(identityVerificationInfo.zip ?? ''),
        };

        return {
          propertyId,
          identityVerificationInfo: payload,
        };
      } catch (err) {
        logError({ error: err, context: 'getVerificationPayload' });
        throw err;
      }
    },
    // Redux actions for post-verification flow
    checkRequestActionSuccess,
    generateMoveInCostPayload,
    editMoveInCosts: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editMoveInCostsPage({ propertyId })))
        .then(() => dispatch(getFeaturePermissions({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.moveInCostsPage,
              state: { fromWhere: 'onboardingReview' },
            }),
          ),
        ),
    editRentAmount: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editRentAmountPage({ propertyId })))
        .then(() =>
          dispatch(
            push({ pathname: baseUrl + paths.onboarding.rentAmountPage, state: { fromWhere: 'onboardingReview' } }),
          ),
        ),
    editDueDate: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editDueDatePage({ propertyId })))
        .then(() =>
          dispatch(
            push({ pathname: baseUrl + paths.onboarding.dueDatePage, state: { fromWhere: 'onboardingReview' } }),
          ),
        ),
    editPaymentTerm: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editPaymentTermPage({ propertyId })))
        .then(() =>
          dispatch(
            push({ pathname: baseUrl + paths.onboarding.termLengthPage, state: { fromWhere: 'onboardingReview' } }),
          ),
        ),

    editAccountInfo: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editAccountInfoPage({ propertyId })))
        .then(() => {
          return dispatch(
            push({
              pathname: baseUrl + paths.onboarding.accountInfoPage,
              state: { fromWhere: 'onboardingReview' },
            }),
          );
        }),
    goToPhoneVerificationPage: () => {
      dispatch(closeNotification());
      dispatch(
        push({
          pathname: baseUrl + paths.onboarding.phoneVerificationPage,
          state: { fromWhere: paths.onboarding.confirmationPage },
        }),
      );
    },
    editIdentityVerification: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editTaxVerificationPage({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.taxVerificationPage,
              state: { fromWhere: 'onboardingReview' },
            }),
          ),
        ),
    editTenantInvitationInfo: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editInviteTenantPage({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.inviteTenantPage,
              state: { fromWhere: 'onboardingReview' },
            }),
          ),
        ),
    onBack: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => {
          const fromPhoneVerification = location?.state?.fromWhere === paths.onboarding.phoneVerificationPage;
          return dispatch(fromPhoneVerification ? go(-2) : goBack());
        }),
    onClose: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup))),
    trackValidationError: (data) => dispatch(trackValidationError(data)),
    checkIsPhoneVerificationNeeded: async ({
      phoneNumberFromOnboarding,
      listingDetailsContactPhone,
    }): Promise<boolean> => {
      try {
        const action = await dispatch(
          requestPhoneVerificationCheck({
            propertyId,
            phoneNumber: cleanPhoneNumber(phoneNumberFromOnboarding || listingDetailsContactPhone),
          }),
        );
        await checkRequestActionSuccess(action);
        if (
          !action.payload ||
          !action.payload.response ||
          !action.payload.response.isSessionValid ||
          !action.payload.response.isVerified
        ) {
          return true;
        }
        return false;
      } catch (e: unknown) {
        const payload = (
          e as {
            action?: {
              payload?: {
                status?: number;
                httpStatus?: number;
                error?: { code?: string; message?: string };
                response?: { error?: { code?: string; message?: string } };
              };
            };
          }
        )?.action?.payload;
        const status = payload?.status ?? payload?.httpStatus;
        const err = payload?.error ?? payload?.response?.error;
        const isPhoneInvalid =
          err &&
          (err.code === 'phone-invalid' || err.message === API_MESSAGES.INVALID_PHONE_ERROR) &&
          (status === HTTP_STATUS.SC_BAD_REQUEST || status === HTTP_STATUS.SC_FORBIDDEN);
        if (payload && isPhoneInvalid) {
          return true;
        }
        throw e;
      }
    },
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  getVerificationPayload: () =>
    dispatchProps.getVerificationPayload({
      identityVerificationInfo: stateProps.onboardingInfo.identityVerificationInfo,
    }),
  checkIsPhoneVerificationNeeded: () =>
    dispatchProps.checkIsPhoneVerificationNeeded({
      phoneNumberFromOnboarding: stateProps.identityVerificationInfo.phoneNumber,
      listingDetailsContactPhone: stateProps.listingDetailsContactPhone,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(OnboardingConfirmationPage);
